<template>
  <VWidget
    v-if="settings && settings.requesting_delivery_allowed"
    :title="$t('Requested Delivery')"
    class="mb-4"
  >
    <v-layout
      slot="widget-content"
      row
      wrap
      class="mx-4"
    >
      <v-menu
        v-model="datePicker"
        :close-on-content-click="false"
        :close-on-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template #activator="{ attrs, on }">
          <v-text-field
            v-model="date"
            v-bind="attrs"
            :label="$t('Delivery date')"
            :class="{ required: dateIsRequired }"
            clearable
            readonly
            prepend-icon="event"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="date"
          :allowed-dates="allowedDates"
          :min="minDeliveryDate"
          @input="datePicker = false"
        >
          <v-btn
            text
            color="success"
            @click="datePicker = false"
          >
            <span>{{ $t('Save') }}</span>
          </v-btn>
          <v-spacer />
          <v-btn
            text
            color="warning"
            @click="onClearDate"
          >
            {{ $t('Clear') }}
          </v-btn>
        </v-date-picker>
      </v-menu>
      <v-autocomplete
        v-if="showDeliveryTime"
        v-model="time"
        :label="$t('Delivery time')"
        :items="availableTimes"
        clearable
        prepend-icon="schedule"
      />
    </v-layout>
  </VWidget>
</template>

<script>

export const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

export default {
  name: 'OrderDraftRequestedDelivery',

  props: {
    value: {
      type: String,
      required: false,
      default: ''
    },
    valid: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data () {
    return {
      datePicker: false,
      settings: null,
      menuHour: false,
      menuDay: false
    }
  },

  computed: {
    date: {
      get () {
        if (!this.value) {
          return ''
        }
        return this.value.split(' ').shift()
      },
      set (value) {
        this.$emit('input', [value, this.time].join(' '))
      }
    },
    time: {
      get () {
        if (!this.value) {
          return ''
        }
        const strings = this.value.split(' ')
        if (strings.length <= 1) {
          return ''
        }

        return strings.pop()
      },
      set (value) {
        this.$emit('input', [this.date, value].join(' '))
      }
    },
    dateIsRequired () {
      return Boolean(this.time || this.date)
    },
    showDeliveryTime () {
      if (!this.settings) {
        return false
      }

      return this.settings.show_delivery_time || false
    },
    excludedDates () {
      if (this.settings && this.settings.delivery_blackouts) {
        return this.settings.delivery_blackouts.dates
      }
      return []
    },
    excludedWeekdays () {
      if (this.settings && this.settings.delivery_blackouts) {
        return this.settings.delivery_blackouts.weekdays
      }
      return []
    },
    excludedHours () {
      if (this.settings && this.settings.delivery_blackouts) {
        return this.settings.delivery_blackouts.hours
      }
      return []
    },
    excludedHoursInMinutes () {
      return this.excludedHours.map((config) => {
        return config.split('-').map((hours) => {
          const [h, m] = hours.split(':').map(Number)
          return h * 60 + m
        })
      })
    },
    availableTimes () {
      const isExcluded = (time) => {
        return this.excludedHoursInMinutes.some((hours) => {
          /**
           * excluded: 02:00 - 19:00
           *
           * open (utc): 19:15 - 01:45
           * open (warsaw): 20:15 - 02:45
           */
          if (hours[0] < hours[1] && time > hours[0] - this.timeInterval && time < hours[1] + this.timeInterval) {
            return true
          }
          /**
           * excluded: 19:00 - 02:00
           *
           * open (utc): 02:15 - 18:45
           * open (warsaw): 03:15 - 19:45
           */
          if (hours[0] > hours[1] && (time > hours[0] - this.timeInterval || time < hours[1] + this.timeInterval)) {
            return true
          }
          return false
        })
      }
      const now = new Date().getUTCHours() * 60 + new Date().getUTCMinutes() + this.timeInterval
      const available = []
      for (let i = 0; i < 1440; i = i + this.timeInterval) {
        if (isExcluded(i) || (this.selectedDayIsToday && now > i)) {
          continue
        }
        available.push(i)
      }
      return available
        .map((t) => {
          let i = t - this.offset
          if (i <= 0) {
            i = 1440 + i
          }
          if (i >= 1440) {
            i = i - 1440
          }
          const hours = Math.floor(i / 60)
          const minutes = (i % 60).toString().padStart(2, '0')
          return [hours.toString().padStart(2, '0'), minutes].join(':')
        })
        .sort((a, b) => a.localeCompare(b))
    },
    minDeliveryDate () {
      let date = new Date()
      if (this.time) {
        const [hours, minutes] = this.time.split(':')
        if (hours <= date.getHours() && minutes <= date.getMinutes() + this.timeInterval) {
          date = new Date(date.getTime() + 24 * 60 * 60 * 1000)
        }
      }
      return date.toISOString().substr(0, 10)
    },
    selectedDayIsToday () {
      return this.isToday(this.date)
    },
    timeInterval () {
      return this.settings.delivery_interval || 15
    },
    offset () {
      return new Date().getTimezoneOffset()
    }
  },

  watch: {
    date: function (val) {
      if (this.time) {
        this.$emit('update:valid', !!val)
        return
      }
      return this.$emit('update:valid', true)
    },
    time: function () {
      this.$emit('update:valid', !!this.date)
    }
  },

  async mounted () {
    this.settings = await this.$http.make('general_settings').find('checkout')
  },

  methods: {
    isToday (value) {
      return value === new Date().toISOString().substr(0, 10)
    },
    allowedDates (value) {
      if (this.isToday(value) && this.time) {
        const now = new Date()
        const [h, m] = this.time.split(':').map(Number)
        if (h <= now.getHours() && m <= now.getUTCMinutes()) {
          return false
        }
      }
      const day = WEEKDAYS[new Date(value).getUTCDay()]
      return !this.excludedDates.includes(value) && !this.excludedWeekdays.includes(day)
    },
    onClearDate () {
      this.date = null
      this.datePicker = false
    }
  }
}
</script>
